import { Fragment, JSX, useState, useEffect } from 'react';
import { ResponsiveContainer } from 'recharts';

import { CaseReportSegment } from '../../models/CaseTypes';
import { getStepColor } from '../../utils/caseUtils';
import { BREAKPOINT_MOBILE } from '../../utils/uiUtils';
import { logTrace } from '../../utils/trackingUtils';

const TIMELINE_CONSTANTS = {
  PADDING: 24,
  SEGMENT_GAP: 4,
  MIN_SEGMENT_HEIGHT: 16,
  MAX_SEGMENT_HEIGHT: 26,
  MOBILE_HEIGHT: 200,
  DESKTOP_HEIGHT: 400,
  AXIS_HEIGHT: 60, // Space for time axis
  LEGEND_HEIGHT: 80 // Space for legend
};

const formatDuration = (startTime: number, endTime: number): string => {
  const durationInMinutes = endTime - startTime;
  const hours = Math.floor(durationInMinutes / 60);
  const minutes = Math.floor(durationInMinutes % 60);

  if (hours > 0) {
    return `${hours}hr ${minutes > 0 ? minutes + 'min' : ''}`.trim();
  }
  return `${minutes}mins`;
};

interface SegmentProps {
  segment: CaseReportSegment;
  mouseX: number;
  mouseY: number;
}

const CaseEfficiencyTimeline = (
  { totalTimeDisplay, segments, onSegmentSelected }:
  { totalTimeDisplay: string, segments: CaseReportSegment[], onSegmentSelected: (segment: CaseReportSegment) => void }
): JSX.Element => {
  // State for touch interaction
  const [touchTimeout, setTouchTimeout] = useState<ReturnType<typeof setTimeout>|null>(null);
  const [isTooltipLocked, setIsTooltipLocked] = useState<boolean>(false);
  const [hoveredSegment, setHoveredSegment] = useState<SegmentProps|null>(null);
  const [maxTime, setMaxTime] = useState(300);

  const handleMouseEnter = (segment: CaseReportSegment, event: { clientX: number; clientY: number }): void => {
    logTrace('CaseEfficiency', 'proprio_segment_hover_' + segment.segmentType, segment.segmentType);
    setHoveredSegment({
      segment,
      mouseX: event.clientX,
      mouseY: event.clientY
    });
  };

  const handleMouseLeave = (): void => {
    setHoveredSegment(null);
  };

  const handleSegmentClick = (segment: CaseReportSegment): void => {
    logTrace('CaseEfficiency', 'proprio_segment_click_' + segment.segmentType, segment.segmentType);
    onSegmentSelected(segment);
  };

  const getTimelineHeight = (): number => {
    if (!segments || segments.length === 0) return TIMELINE_CONSTANTS.DESKTOP_HEIGHT;

    const numSegments = segments.length;
    const segmentHeight = Math.min(
      Math.max(TIMELINE_CONSTANTS.MIN_SEGMENT_HEIGHT, TIMELINE_CONSTANTS.MIN_SEGMENT_HEIGHT),
      TIMELINE_CONSTANTS.MAX_SEGMENT_HEIGHT
    );

    // Calculate the total height needed for all segments plus padding and axis
    const totalHeight = (segmentHeight * numSegments) +
                       (TIMELINE_CONSTANTS.PADDING * 2) +
                       TIMELINE_CONSTANTS.AXIS_HEIGHT;

    // For mobile, use a reasonable minimum but allow it to grow
    const isMobile = window.innerWidth <= 390;
    const minHeight = isMobile ? TIMELINE_CONSTANTS.MOBILE_HEIGHT : TIMELINE_CONSTANTS.DESKTOP_HEIGHT;

    // Always ensure we have enough height for all segments
    return Math.max(totalHeight, minHeight);
  };

  const handleTouchStart = (segment: CaseReportSegment, event: React.TouchEvent): void => {
    event.preventDefault();
    setTouchTimeout(setTimeout(() => {
      setIsTooltipLocked(true);
      handleMouseEnter(segment, {
        clientX: event.touches[0].clientX,
        clientY: event.touches[0].clientY
      });
    }, 500));
  };

  const handleTouchEnd = (): void => {
    if (touchTimeout) {
      clearTimeout(touchTimeout);
      setTouchTimeout(null);
    }
    if (!isTooltipLocked) {
      handleMouseLeave();
    }
  };

  const handleTouchMove = (): void => {
    if (touchTimeout) {
      clearTimeout(touchTimeout);
      setTouchTimeout(null);
    }
  };

  useEffect(() => {
    // Calculate max segment time.
    if (segments && segments.length > 0) {
      setMaxTime(Math.max(...segments.map(segment => segment.normalizedEndMin)));
    }
  }, [segments]);

  if (!segments || segments.length === 0) {
    return (
      <div className="case-timeline-container">
        <div className="timeline-section">
          <div className="section-title">Case Timeline</div>
          <div>There are no time segments available for this case.</div>
        </div>
      </div>
    );
  }

  return (
    <div className="case-timeline-container">
      <div className="timeline-section">
        <div className="section-title">Case Timeline</div>
        <div className="total-time-container">
          <div className="total-time-value">{totalTimeDisplay}</div>
          <div className="total-time-label">Total Navigated Time:</div>
        </div>

        <div style={{
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: '#ffffff',
          borderRadius: '12px',
          overflow: 'hidden',
          border: '1px solid #E5E7EB'
        }}>
          <div style={{
            overflowX: 'auto',
            overflowY: 'hidden',
            scrollbarWidth: 'thin'
          }}>
            <ResponsiveContainer width="100%" height={getTimelineHeight()} minWidth={800}>
              <div style={{
                position: 'relative',
                height: '100%',
                backgroundColor: '#ffffff',
                borderRadius: '12px',
                minWidth: '800px'
              }}>
                {/* Calculate grid and segment layout */}
                {((): React.JSX.Element => {
                  const numSegments = segments?.length || 0;
                  const totalTimelineHeight = getTimelineHeight();
                  const chartAreaHeight =
                    totalTimelineHeight - TIMELINE_CONSTANTS.AXIS_HEIGHT - (TIMELINE_CONSTANTS.PADDING * 2);

                  // Calculate segment height based on available space
                  const segmentHeight = Math.min(
                    Math.max(chartAreaHeight / numSegments, TIMELINE_CONSTANTS.MIN_SEGMENT_HEIGHT),
                    TIMELINE_CONSTANTS.MAX_SEGMENT_HEIGHT
                  );

                  return (
                    <div style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      padding: `${TIMELINE_CONSTANTS.PADDING}px`
                    }}>
                      {/* Chart background */}
                      <div style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: segmentHeight * numSegments,
                        backgroundColor: '#FAFAFA',
                        border: '1px solid #E5E7EB',
                        borderRadius: '4px'
                      }} />

                      {/* Horizontal grid lines */}
                      {[...segments].reverse().map((_, index) => (
                        <div
                          key={`h-grid-${index}`}
                          style={{
                            position: 'absolute',
                            top: `${index * segmentHeight}px`,
                            left: 0,
                            right: 0,
                            height: '1px',
                            backgroundColor: index === 0 ? '#D1D5DB' : '#E5E7EB',
                            zIndex: 1
                          }}
                        />
                      ))}

                      {/* Vertical grid lines */}
                      {Array.from({ length: Math.ceil(maxTime / 60) + 2 }).map((_, i) => {
                        const timeValue = i * 60;
                        const leftPercent = (timeValue / maxTime) * 100;
                        // Don't render grid lines beyond 100%
                        if (leftPercent > 100) return null;
                        return (
                          <div
                            key={`v-grid-${i}`}
                            style={{
                              position: 'absolute',
                              left: `calc(${leftPercent}% - 1px)`,
                              top: 0,
                              height: segmentHeight * numSegments,
                              width: '1px',
                              backgroundColor: i === 0 ? '#D1D5DB' : '#E5E7EB',
                              zIndex: 1
                            }}
                          />
                        );
                      })}

                      {/* Timeline segments */}
                      {[...segments].reverse().map((segment, index) => {
                        // Position segments based on their actual start/end times
                        const leftPercent = (segment.normalizedStartMin / maxTime) * 100;
                        const widthPercent =
                          Math.max(((segment.normalizedEndMin - segment.normalizedStartMin) / maxTime) * 100, 0.5);

                        return (
                          <div
                            key={`segment-${index}`}
                            data-testid={`segment-${index}`}
                            style={{
                              position: 'absolute',
                              left: `${leftPercent}%`,
                              top: `${(index * segmentHeight) + (TIMELINE_CONSTANTS.SEGMENT_GAP / 2)}px`,
                              width: `${widthPercent}%`,
                              height: `${segmentHeight - TIMELINE_CONSTANTS.SEGMENT_GAP}px`,
                              backgroundColor: getStepColor(segment.segmentType),
                              borderRadius: '3px',
                              cursor: 'pointer',
                              zIndex: 5,
                              transition: 'all 0.2s ease',
                              transform: hoveredSegment?.segment.segmentType === segment.segmentType ? 'scale(1.02)' : 'scale(1)',
                              boxShadow: hoveredSegment?.segment.segmentType === segment.segmentType ?
                                '0 4px 12px rgba(0,0,0,0.15)' : '0 1px 3px rgba(0,0,0,0.1)',
                              border: '1px solid rgba(255,255,255,0.3)'
                            }}
                            onMouseEnter={e => !isTooltipLocked && handleMouseEnter(segment, e)}
                            onMouseLeave={() => !isTooltipLocked && handleMouseLeave()}
                            onClick={() => handleSegmentClick(segment)}
                            onTouchStart={e => handleTouchStart(segment, e)}
                            onTouchEnd={handleTouchEnd}
                            onTouchMove={handleTouchMove}
                          />
                        );
                      })}

                      {/* Time axis */}
                      <div style={{
                        position: 'absolute',
                        left: 0,
                        right: 0,
                        top: segmentHeight * numSegments + 16,
                        height: TIMELINE_CONSTANTS.AXIS_HEIGHT - 16,
                        borderTop: '2px solid #374151',
                        paddingLeft: '10px', // Add padding to prevent cutoff of "0" label
                        paddingRight: '10px'
                      }}>
                        {/* Hour markers and labels */}
                        {Array.from({ length: Math.ceil(maxTime / 60) + 2 }).map((_, i) => {
                          const timeValue = i * 60;
                          const leftPercent = (timeValue / maxTime) * 100;
                          const hourLabel = Math.floor(timeValue / 60);
                          // Don't render labels beyond 100%
                          if (leftPercent > 100) return null;
                          return (
                            <div key={i}>
                              {/* Tick mark */}
                              <div style={{
                                position: 'absolute',
                                left: `${leftPercent}%`,
                                top: -2,
                                width: '1px',
                                height: '8px',
                                backgroundColor: '#374151',
                                transform: 'translateX(-50%)'
                              }} />

                              {/* Hour label */}
                              <div style={{
                                position: 'absolute',
                                left: `${leftPercent}%`,
                                top: '12px',
                                transform: i === 0 ? 'translateX(2px)' : 'translateX(-50%)', // Special case for "0" to prevent cutoff
                                fontSize: window.innerWidth <= BREAKPOINT_MOBILE ? '11px' : '12px',
                                color: '#374151',
                                fontWeight: '500',
                                minWidth: '20px',
                                textAlign: i === 0 ? 'left' : 'center' // Left align the "0" label
                              }}>
                                {hourLabel}
                              </div>
                            </div>
                          );
                        })}

                        {/* Axis label */}
                        <div style={{
                          position: 'absolute',
                          bottom: 0,
                          left: '50%',
                          transform: 'translateX(-50%)',
                          fontSize: window.innerWidth <= BREAKPOINT_MOBILE ? '12px' : '13px',
                          color: '#374151',
                          fontWeight: '600'
                        }}>
                          Time (Hours)
                        </div>
                      </div>

                      {/* Add the tooltip */}
                      {hoveredSegment && (
                        <TimelineTooltip
                          segment={hoveredSegment.segment}
                          mouseX={hoveredSegment.mouseX}
                          mouseY={hoveredSegment.mouseY}
                          isLocked={isTooltipLocked}
                          onClose={() => setIsTooltipLocked(false)}
                        />
                      )}
                    </div>
                  );
                })()}
              </div>
            </ResponsiveContainer>
          </div>

          {/* Legend outside the chart area */}
          {segments && (
            <TimelineLegend segments={segments} />
          )}
        </div>
      </div>
    </div>
  );
};

// Add this new component before CaseEfficiency
const TimelineLegend = ({ segments }: { segments: CaseReportSegment[]}): React.JSX.Element => {
  const uniqueTypes = [...new Set(segments.map((segment: CaseReportSegment) => segment.segmentType))];

  return (
    <div style={{
      display: 'flex',
      flexWrap: 'wrap',
      justifyContent: 'center',
      alignItems: 'center',
      gap: '16px',
      padding: '16px 24px',
      backgroundColor: '#F9FAFB',
      borderTop: '1px solid #E5E7EB'
    }}>
      {uniqueTypes.map((type, index) => (
        <div
          key={index}
          style={{
            display: 'flex',
            alignItems: 'center',
            fontSize: window.innerWidth <= BREAKPOINT_MOBILE ? '12px' : '13px',
            color: '#374151',
            fontWeight: '500'
          }}
        >
          <div style={{
            width: '12px',
            height: '12px',
            backgroundColor: getStepColor(type),
            marginRight: '8px',
            borderRadius: '2px',
            border: '1px solid rgba(255,255,255,0.3)',
            boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
          }} />
          <span>{type}</span>
        </div>
      ))}
    </div>
  );
};

// Then update the TimelineTooltip component
const TimelineTooltip = (
  { segment, mouseX, mouseY, isLocked, onClose } :
  { segment: CaseReportSegment, mouseX: number, mouseY: number, isLocked: boolean, onClose: () => void }
): React.JSX.Element => {
  if (!segment) return <Fragment></Fragment>;

  return (
    <div
      data-testid="timeline-tooltip"
      style={{
        position: 'fixed',
        left: mouseX + 12,
        top: mouseY - 10,
        backgroundColor: 'rgba(255, 255, 255, 0.98)',
        padding: '12px 16px',
        borderRadius: '8px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
        zIndex: 1000,
        pointerEvents: isLocked ? 'auto' : 'none',
        fontSize: '13px',
        border: '1px solid rgba(0,0,0,0.1)',
        maxWidth: '200px',
        backdropFilter: 'blur(4px)'
      }}
      onClick={() => onClose()}
    >
      {/* Add a close button when locked */}
      {isLocked && (
        <button
          style={{
            position: 'absolute',
            top: 6,
            right: 8,
            padding: 4,
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            fontSize: '14px',
            color: '#9CA3AF',
            fontWeight: 'bold'
          }}
          onClick={e => {
            e.stopPropagation();
            onClose();
          }}
        >
          ×
        </button>
      )}
      <div style={{
        fontWeight: 600,
        marginBottom: '6px',
        color: '#111827',
        fontSize: '14px'
      }}>
        {segment.segmentType}
      </div>
      {segment.segmentValue && (
        <div style={{
          color: '#6B7280',
          marginBottom: '4px',
          fontSize: '12px'
        }}>
          {segment.segmentValue}
        </div>
      )}
      <div style={{
        color: getStepColor(segment.segmentType),
        marginTop: '4px',
        fontWeight: '600',
        fontSize: '12px'
      }}>
        Duration: {formatDuration(segment.normalizedStartMin, segment.normalizedEndMin)}
      </div>
    </div>
  );
};

export default CaseEfficiencyTimeline;
